/**
 * @fileoverview System form service — local-first CRUD for system-scoped forms.
 * @module services/systemFormService
 *
 * Responsibilities:
 * - CRUD operations for system forms in Dexie.
 * - Publish/archive/restore lifecycle management.
 * - Clone, favorites/library helpers.
 * - Audit logging for all mutations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (runAuditedMutation)
 * - ./systemAuditLogService (writeAuditLog)
 *
 * Architecture:
 * - Local-first: Dexie-first reads/writes + wakeLocalWrite for push sync.
 * - API surface mirrors tenant-web formServiceWithFallback for canvas reuse.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - publish/archive/restore/cloneForm delegate to createForm or updateForm
 *   and are therefore audited through those paths.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { runAuditedMutation } from '../../../packages/audit/src';
import { writeAuditLog } from './systemAuditLogService';
import type {
  TemplateBuilderForm, FormStatus, TemplateType, FormField, DataBoundWidgetDefinition,
} from '@firesquirrel/ui-admin';

// ---------------------------------------------------------------------------
// List params / result (mirrors tenant-web FormListParams / FormListResult)
// ---------------------------------------------------------------------------

export interface FormListParams {
  search?: string;
  status?: FormStatus;
  templateType?: TemplateType;
  reusable?: boolean;
  includeArchived?: boolean;
  category?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface FormListResult {
  items: TemplateBuilderForm[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return 'sfrm-' + Math.random().toString(36).substring(2, 10);
}

function generateFieldId(): string {
  return 'fld_' + Math.random().toString(36).substring(2, 8);
}

// ---------------------------------------------------------------------------
// Pure field/widget utilities — same as formMockService, shared with canvas
// ---------------------------------------------------------------------------

export function generateSectionId(): string {
  return 'sec-' + Math.random().toString(36).substring(2, 8);
}

export function createBlankField(type: FormField['type']): FormField {
  const id = generateFieldId();
  const labels: Record<string, string> = {
    toggle: 'New Toggle', text: 'New Text Field', textarea: 'New Text Area',
    number: 'New Number', measurement: 'New Measurement', dropdown: 'New Dropdown',
    checkboxGroup: 'New Checkbox Group', radioGroup: 'New Radio Group',
    camera: 'New Camera', date: 'New Date', dateTime: 'New Date & Time',
    photo: 'New Photo', signature: 'New Signature', location: 'New Location',
    section: 'New Section', instructions: 'General Information', repeatableList: 'New List',
    dataBound: 'New Data Field',
  };
  const field: FormField = {
    id,
    type,
    label: labels[type] || 'New Field',
    required: false,
  };
  if (type === 'dropdown' || type === 'checkboxGroup' || type === 'radioGroup') {
    field.extras = { options: [] };
  }
  if (type === 'section') {
    field.extras = { columns: 1 };
  }
  return field;
}

export function createWidgetField(widget: DataBoundWidgetDefinition): FormField {
  const id = generateFieldId();
  return {
    id,
    type: 'dataBound',
    label: widget.label,
    required: false,
    extras: {
      widgetId: widget.widgetId,
      widgetCategory: widget.widgetCategory,
      dataSource: widget.dataSource,
      boundField: widget.boundField,
      ...(widget.compositeFields ? { compositeFields: widget.compositeFields } : {}),
      displayFormat: widget.displayFormat,
      mode: widget.defaultMode,
      inputType: widget.inputType,
      ...(widget.lookupType ? { lookupType: widget.lookupType } : {}),
      ...(widget.requiredModule ? { requiredModule: widget.requiredModule } : {}),
      ...(widget.entityCategoryFilter ? { entityCategoryFilter: widget.entityCategoryFilter } : {}),
      ...(widget.forceReadOnly ? { forceReadOnly: true } : {}),
    },
  };
}

// ---------------------------------------------------------------------------
// List — Dexie first, background API sync
// ---------------------------------------------------------------------------

export async function listForms(params?: FormListParams): Promise<FormListResult> {
  const db = await getSystemDb();
  const allDocs = await db.system_forms.toArray();
  let filtered: TemplateBuilderForm[] = (allDocs as TemplateBuilderForm[])
    .filter((f) => !(f as any)._deleted);

  if (!params?.includeArchived) {
    filtered = filtered.filter((f) => f.status !== 'archived');
  }
  if (params?.search) {
    const term = params.search.toLowerCase();
    filtered = filtered.filter(
      (f) =>
        f.name.toLowerCase().includes(term) ||
        (f.description || '').toLowerCase().includes(term),
    );
  }
  if (params?.status) {
    filtered = filtered.filter((f) => f.status === params.status);
  }
  if (params?.templateType) {
    filtered = filtered.filter((f) => f.templateType === params.templateType);
  }
  if (params?.reusable !== undefined) {
    filtered = filtered.filter((f) => f.reusable === params.reusable);
  }
  if (params?.category) {
    filtered = filtered.filter((f) => f.category === params.category);
  }

  const sortBy = params?.sortBy || 'updatedAt';
  const sortDir = params?.sortOrder === 'asc' ? 1 : -1;
  filtered.sort((a, b) => {
    const aVal = (a as any)[sortBy] ?? '';
    const bVal = (b as any)[sortBy] ?? '';
    if (aVal < bVal) return -1 * sortDir;
    if (aVal > bVal) return 1 * sortDir;
    return 0;
  });

  const page = params?.page || 1;
  const limit = params?.limit || 25;
  const start = (page - 1) * limit;

  return { items: filtered.slice(start, start + limit), total: filtered.length, page, limit };
}

// ---------------------------------------------------------------------------
// Get single — Dexie first, API fallback
// ---------------------------------------------------------------------------

export async function getFormById(id: string): Promise<TemplateBuilderForm | null> {
  const db = await getSystemDb();
  const doc = await db.system_forms.get(id);
  return doc ? doc as TemplateBuilderForm : null;
}

// ---------------------------------------------------------------------------
// Create — Dexie optimistic, then API write-through
// ---------------------------------------------------------------------------

/** @process createForm — Dexie mutation + audit entry. */
export async function createForm(data: Partial<TemplateBuilderForm>): Promise<TemplateBuilderForm> {
  const now = new Date().toISOString();
  const newId = generateId();
  const form: TemplateBuilderForm = {
    _id: newId,
    name: data.name || 'Untitled Form',
    description: data.description,
    templateType: data.templateType || 'fireCode',
    templateTypeName: data.templateTypeName || 'Fire Code',
    category: data.category || 'general',
    formKind: data.formKind,
    moduleConfig: data.moduleConfig,
    version: 1,
    status: 'draft',
    fields: data.fields || [],
    layout: data.layout || { sections: [] },
    display: data.display,
    initialVisibility: data.initialVisibility,
    rules: data.rules || [],
    reusable: data.reusable ?? false,
    createdAt: now,
    createdBy: data.createdBy || 'system-user',
    updatedAt: now,
    updatedBy: data.updatedBy || 'system-user',
  };

  const db = await getSystemDb();

  return runAuditedMutation<null, TemplateBuilderForm>({
    collection: 'systemForms',
    documentId: newId,
    action: 'create',
    summary: `Created system form: ${form.name}`,
    getBefore: async () => null,
    execute: async () => {
      await db.system_forms.put(form as any);
      wakeLocalWrite('system_forms');
      return form;
    },
    getAfter: async () => (await db.system_forms.get(newId)) as TemplateBuilderForm | null,
    writeAuditLog,
  });
}

// ---------------------------------------------------------------------------
// Update — Dexie optimistic, then API write-through
// ---------------------------------------------------------------------------

/** @process updateForm — Dexie mutation + audit entry. */
export async function updateForm(id: string, patch: Partial<TemplateBuilderForm>): Promise<TemplateBuilderForm> {
  const db = await getSystemDb();
  const existing = await db.system_forms.get(id);
  if (!existing) throw new Error(`Form ${id} not found`);

  return runAuditedMutation<TemplateBuilderForm, TemplateBuilderForm>({
    collection: 'systemForms',
    documentId: id,
    action: 'update',
    summary: `Updated system form: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    execute: async () => {
      const updates = { ...patch, updatedAt: new Date().toISOString() };
      const updated = { ...(existing as any), ...updates } as TemplateBuilderForm;
      await db.system_forms.put(updated as any);
      wakeLocalWrite('system_forms');
      return updated;
    },
    getAfter: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    writeAuditLog,
  });
}

// ---------------------------------------------------------------------------
// Publish
// ---------------------------------------------------------------------------

/** @process publishForm — delegates to audited mutation. */
export async function publishForm(id: string, _changeNotes?: string): Promise<TemplateBuilderForm> {
  return updateForm(id, { status: 'published', publishedAt: new Date().toISOString() });
}

// ---------------------------------------------------------------------------
// Archive / Restore
// ---------------------------------------------------------------------------

/** @process archiveForm — delegates to audited mutation. */
export async function archiveForm(id: string): Promise<TemplateBuilderForm> {
  return updateForm(id, { status: 'archived' });
}

export async function restoreForm(id: string): Promise<TemplateBuilderForm> {
  return updateForm(id, { status: 'draft' });
}

// ---------------------------------------------------------------------------
// Clone
// ---------------------------------------------------------------------------

/** @process cloneForm — delegates to audited mutation. */
export async function cloneForm(id: string, name?: string): Promise<TemplateBuilderForm> {
  const source = await getFormById(id);
  if (!source) throw new Error(`Form not found: ${id}`);
  const { _id, createdAt, updatedAt, ...rest } = source;
  return createForm({ ...rest, name: name || `${source.name} (Copy)`, status: 'draft', version: 1 });
}

// ---------------------------------------------------------------------------
// Delete (hard delete from Dexie + API)
// ---------------------------------------------------------------------------

/** @process deleteForm — Dexie mutation + audit entry. */
export async function deleteForm(id: string): Promise<{ deleted: boolean }> {
  const db = await getSystemDb();
  const existing = await db.system_forms.get(id);
  if (!existing) return { deleted: false };

  await runAuditedMutation<TemplateBuilderForm, TemplateBuilderForm>({
    collection: 'systemForms',
    documentId: id,
    action: 'delete',
    summary: `Deleted system form: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    execute: async () => {
      const tombstone = { ...(existing as any), _deleted: true, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      await db.system_forms.put(tombstone as any);
      wakeLocalWrite('system_forms');
      return tombstone as TemplateBuilderForm;
    },
    getAfter: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    writeAuditLog,
  });

  return { deleted: true };
}

// ---------------------------------------------------------------------------
// Favorites / Library helpers — Dexie-only (no API equivalent yet)
// ---------------------------------------------------------------------------

/** @process toggleFormFavorite — Dexie mutation + audit entry. */
export async function toggleFormFavorite(id: string): Promise<TemplateBuilderForm | null> {
  const db = await getSystemDb();
  const existing = await db.system_forms.get(id);
  if (!existing) return null;

  return runAuditedMutation<TemplateBuilderForm, TemplateBuilderForm>({
    collection: 'systemForms',
    documentId: id,
    action: 'update',
    summary: `Toggled reusable flag on system form: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    execute: async () => {
      const updated = { ...(existing as any), reusable: !(existing as any).reusable, updatedAt: new Date().toISOString() };
      await db.system_forms.put(updated);
      wakeLocalWrite('system_forms');
      return updated as TemplateBuilderForm;
    },
    getAfter: async () => (await db.system_forms.get(id)) as TemplateBuilderForm | null,
    writeAuditLog,
  });
}

export async function countLibraryCopies(_id: string): Promise<number> {
  return 0;
}

export async function propagateLibraryUpdate(_id: string): Promise<number> {
  return 0;
}
